/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import { useState, useEffect } from "react";
import { Image, View } from "react-native";
import { NavLink } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import { ActionCreators } from "actions";
import { RootState } from "store/types";

import { styles } from "UpduoLibrary";
import NavigationBarItem from "./NavigationBarItem";
import OrgSelector from "./OrgSelector";
import { withApollo, WithApolloClient } from "@apollo/client/react/hoc";
import { _handlePrimaryMemberLogin } from "config/ResetState";
import UpduoLogo from "assets/upduo-black.svg";
import AccountButton from "./AccountButton";

function NavigationBar(props: WithApolloClient<{}>) {
  const dispatch = useDispatch();
  const primaryAuthToken = useSelector(
    (state: RootState) => state.primaryAuthToken
  );
  const selfObject = useSelector((state: RootState) => state.selfObject);
  const primaryMemberList = useSelector(
    (state: RootState) => state.primaryMemberList
  );
  const selectedSummit = useSelector(
    (state: RootState) => state.selectedSummit
  );

  const [dashboardLink, setDashboardLink] = useState("/dashboard");

  useEffect(() => {
    // If user is a leader of a region/district/store, set it as user's default dashboard view
    let newDashboardLink = "/dashboard";
    if (selfObject.leaderSummary?.type && selfObject.leaderSummary.isLeader) {
      const type = selfObject.leaderSummary.type.toLowerCase();
      newDashboardLink = `/dashboard/${type}/${selfObject.leaderSummary.id}`;
    }
    setDashboardLink(newDashboardLink);
  }, [selfObject.leaderSummary]);

  // Hack for "library" organizations:
  // Only Content page for non-Maintainance users, plus Members page and editing only for Maintenance users:
  const libraryOrganizationIds =
    process.env.REACT_APP_LIBRARY_ORGANIZATION_IDS?.split(",") || [];
  const isLibraryOrganization = libraryOrganizationIds.includes(
    selfObject?.organizationId
  );
  if (isLibraryOrganization) {
    return (
      <View style={styles.navigationBar}>
        <NavLink
          to={"/"}
          style={{
            flexDirection: "row",
            alignItems: "center",
            textDecoration: "none",
          }}
        >
          <Image
            source={{ uri: UpduoLogo }}
            resizeMode={"contain"}
            style={{ width: 140, height: 40, marginBottom: 1, marginRight: 20 }}
          />
        </NavLink>
        {primaryAuthToken && selfObject.firstName ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              flex: 1,
            }}
          >
            <OrgSelector
              currentOrgID={selfObject.userId}
              onChange={async (userId: string, callback: () => void) => {
                if (selfObject.userId !== userId) {
                  dispatch(ActionCreators.setDisplayedSummitView("Summit"));
                  await _handlePrimaryMemberLogin(userId, callback);
                  props.client?.stop();
                  await props.client?.clearStore();
                }
              }}
              optionList={primaryMemberList}
            />
            <NavigationBarItem itemName={"Content"} to={"/"} />
            {selfObject.storeName === "Maintenance" ? (
              <NavigationBarItem itemName={"Members"} to={"/members"} />
            ) : null}
            <View style={{ flex: 1 }} />
            <AccountButton selfObject={selfObject} />
          </View>
        ) : null}
      </View>
    );
  }

  // Need to support both selectedSummit.organization.org_code and selectedSummit.organization.orgCode for now
  // (due to use of getSummitDetailWithCode REST API)
  const orgCode =
    selectedSummit?.organization &&
    // @ts-ignore
    (selectedSummit?.organization?.org_code !==
      selectedSummit?.organization?.domain ||
      selectedSummit?.organization?.orgCode !==
        selectedSummit?.organization?.domain)
      ? // @ts-ignore
        selectedSummit?.organization?.org_code ||
        selectedSummit?.organization?.orgCode
      : "";

  return (
    <View style={styles.navigationBar}>
      <NavLink
        to={selectedSummit?.id ? `/start/${orgCode}` : "/"}
        style={{
          flexDirection: "row",
          alignItems: "center",
          textDecoration: "none",
        }}
      >
        <Image
          source={{ uri: UpduoLogo }}
          resizeMode={"contain"}
          style={{ width: 140, height: 40, marginBottom: 1, marginRight: 20 }}
        />
      </NavLink>
      {primaryAuthToken && selfObject.firstName ? (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            flex: 1,
          }}
        >
          <OrgSelector
            currentOrgID={selfObject.userId}
            onChange={async (userId: string, callback: () => void) => {
              if (selfObject.userId !== userId) {
                dispatch(ActionCreators.setDisplayedSummitView("Summit"));
                await _handlePrimaryMemberLogin(userId, callback);
                props.client?.stop();
                await props.client?.clearStore();
              }
            }}
            optionList={primaryMemberList}
          />
          {
            // Show the App tab to any user who sees any other tabs so they can navigate back
            selfObject.isManager ||
            selfObject.isStaff ||
            selfObject.isTrainer ? (
              <NavigationBarItem itemName={"App"} to={"/"} exact={true} />
            ) : null
          }
          {selfObject.isManager ? (
            <NavigationBarItem itemName={"Dashboard"} to={dashboardLink} />
          ) : null}
          {selfObject.isManager &&
          selfObject.organizationManagerDashboardEnabled ? (
            <NavigationBarItem itemName={"Quotas"} to={"/quota-progress"} />
          ) : null}
          {selfObject.isManager ? (
            <NavigationBarItem itemName={"Kudos"} to={"/kudos"} />
          ) : null}
          {selfObject.isStaff || selfObject.isTrainer ? (
            <NavigationBarItem itemName={"Content"} to={"/content"} />
          ) : null}
          {selfObject.isStaff ? (
            <NavigationBarItem itemName={"Events"} to={"/events"} />
          ) : null}
          {selfObject.isStaff ? (
            <NavigationBarItem
              itemName={"Notifications"}
              to={"/notifications"}
            />
          ) : null}
          {selfObject.isStaff ? (
            <NavigationBarItem itemName={"Members"} to={"/members"} />
          ) : null}
          {selfObject.isStaff && selfObject.organizationIsPerformanceEnabled ? (
            <NavigationBarItem itemName={"Performance"} to={"/performance"} />
          ) : null}
          <View style={{ flex: 1 }} />
          <AccountButton selfObject={selfObject} />
        </View>
      ) : null}
    </View>
  );
}

export default withApollo(NavigationBar);
